<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import type { HTMLAttributes } from 'vue'
import { Loader } from 'lucide'
import Select from './internal/Select.vue';
import SelectContent from './internal/SelectContent.vue';
import SelectItem from './internal/SelectItem.vue';
import type { SelectRootEmits } from 'reka-ui'
import SelectTrigger from './internal/SelectTrigger.vue';
import SelectValue from './internal/SelectValue.vue';
import { SphIcon } from '@/components/base/icon/'
import { SphTagGroup } from '@/components/base/tag-group/';
import { useDeferredRef } from '@/composables/deferred-ref'
import { useForwardProps } from 'reka-ui'
import { useI18n } from '@/composables/i18n';


defineOptions({
    compatConfig: {
        MODE: 3,
    },
    inheritAttrs: false
})

interface SelectOption {
    value: any
    label: string
}

const { i18n } = useI18n()

const props = withDefaults(defineProps<{
    class?: HTMLAttributes['class']
    options?: SelectOption[]
    placeholder?: string
    clearable?: boolean
    nullOption?: string | boolean
    multiple?: boolean | { maxItems: false | number, hideSelected: boolean, noTags?: boolean }
    id?: string
    loading?: boolean
    disabled?: boolean
    required?: boolean
    invalid?: boolean
}>(),{
    class: '',
    options: () => [],
    placeholder: undefined,
    clearable: false,
    nullOption: '',
    multiple: false,
    id: undefined,
    loading: false,
    disabled: false,
    required: false,
    invalid: false,
})

const nullOptionLabel = computed(() => {
    if (props.nullOption === false) {
        return null
    }

    if (props.nullOption) {
        if (typeof props.nullOption === 'string') {
            return props.nullOption
        }
    }
    return i18n.t('Keine Auswahl')
})

defineEmits<SelectRootEmits>()

type SelectValueType = string | number
const modelValue = defineModel<SelectValueType | SelectValueType[]>('modelValue')

const selectedOptions = computed(() => {
    const raw = modelValue.value
    const values: SelectValueType[] = Array.isArray(raw)
        ? raw
        : (raw === undefined || raw === '' ? [] : [raw])

    return props.options?.filter(option => values.includes(option.value)) || []
})

const remainingOptions = computed(() => {
    const selectedValues = selectedOptions.value.map(option => option.value)
    return props.options?.filter(option => !selectedValues.includes(option.value)) || []
})

function computeVisible(): SelectOption[] {
    const hideSelected = typeof props.multiple === 'object' && props.multiple.hideSelected
    return hideSelected ? remainingOptions.value : props.options
}

const visibleOptions = ref(computeVisible())

const showTags = computed(() => {
    return props.multiple && (typeof props.multiple !== 'object' || props.multiple.noTags !== true)
})

const placeholder = computed(() => {
    return props.placeholder !== undefined ? props.placeholder : i18n.t('Bitte auswählen')
})

const forwardedProps = computed(() => ({
    placeholder: placeholder,
    multiple: !!props.multiple,
    disabled: props.disabled,
    required: props.required,
}))
const forwarded = useForwardProps(forwardedProps)

const isOpen = ref(false)
const suppressNextFocus = ref(false)

watch(isOpen, (val) => {
    if (!val) {
        suppressNextFocus.value = true
        visibleOptions.value = computeVisible()
    }
}, { flush: 'sync' })

watch(remainingOptions, () => {
    if (!isOpen.value) {
        visibleOptions.value = computeVisible()
    }
})

function handleFocus() {
    if (suppressNextFocus.value) {
        suppressNextFocus.value = false
        return
    }

    isOpen.value = true
}

// when the browser window/tab regains focus, it may re-fire a native `focus`
// event on the previously focused trigger before any user interaction lands.
// suppress that spurious focus so the menu doesn't pop open unexpectedly.
function handleWindowFocus() {
    suppressNextFocus.value = true
}

onMounted(() => {
    window.addEventListener('focus', handleWindowFocus)
})

onUnmounted(() => {
    window.removeEventListener('focus', handleWindowFocus)
})

// loading state with start-delay to prevent flickering on fast operations
const internalLoading = useDeferredRef(() => props.loading, { delayOn: 600, delayOff: 0 })

// display
const displayValue = computed(() => {
    if (props.multiple) {
        const count = selectedOptions.value.length
        if (!count) {
            return undefined
        }
        return i18n.t('{count} ausgewählt', { count })
    } else {
        return selectedOptions.value[0]?.label || ''
    }
})

// tags representation for multiple select mode
const tags = computed(() =>
    selectedOptions.value.map(option => ({ value: option.value, label: option.label }))
)

function deselectOption(tag: { value?: any }) {
    if (!props.multiple || !Array.isArray(modelValue.value)) return

    const currentValue = modelValue.value || []
    if (Array.isArray(currentValue)) {
        const newValue = currentValue.filter((v: any) => v !== tag.value)
        modelValue.value?.splice(0, modelValue.value.length, ...(newValue as any))
    }
}

function onSelect() {
}

function onClear() {
    if (props.multiple) {
        modelValue.value = []
    } else {
        modelValue.value = ''
    }
}
</script>

<template>
    <div
        class="sph-select max-w-full"
        :class="props.class"
    >
        <Select
            v-bind="forwarded"
            v-model:open="isOpen"
            :model-value="modelValue"
            @update:model-value="(val) => { modelValue = val === null ? (props.multiple ? [] : '') : (val as SelectValueType | SelectValueType[]) }"
        >
            <SelectTrigger
                :id="props.id"
                :has-clear-button="props.clearable"
                :show-clear-button="selectedOptions.length > 0"
                :aria-invalid="props.invalid ? 'true' : undefined"
                :placeholder="placeholder"
                @focus="handleFocus"
                @clear="onClear"
            >
                <transition-group
                    name="smooth-list"
                    tag="div"
                    class="flex items-center gap-2 min-w-0"
                >
                    <SphIcon
                        v-if="internalLoading"
                        key="loader"
                        :icon="Loader"
                        size="sm"
                        class="[&>svg]:animate-[spin_1.25s_linear_infinite]"
                    />
                    <SelectValue
                        key="value"
                        class="select-none block flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
                        :placeholder="placeholder"
                    >
                        <template
                            v-if="displayValue"
                        >
                            <slot
                                name="display"
                                :options="selectedOptions"
                            >
                                {{ displayValue }}
                            </slot>
                        </template>
                    </SelectValue>
                </transition-group>
            </SelectTrigger>
            <SelectContent>
                <SelectItem
                    v-if="!props.multiple && nullOptionLabel"
                    :value="null"
                    :class="'text-muted-foreground'"
                >
                    {{ nullOptionLabel }}
                </SelectItem>
                <template
                    v-for="option in visibleOptions"
                    :key="option.value"
                >
                    <SelectItem
                        :value="option.value"
                        @select="onSelect"
                    >
                        <slot
                            name="option-label"
                            :option="option"
                        >
                            {{ option.label }}
                        </slot>
                    </SelectItem>
                </template>
            </SelectContent>
        </Select>

        <SphTagGroup
            v-if="showTags"
            collapse-content-class="pt-2"
            :model-value="tags"
            :deletable="true"
            @delete="deselectOption"
        />
    </div>
</template>


<style scoped>
.smooth-list-enter-from {
    opacity: 0;
    transform: translate(50%, 0);
}

.smooth-list-leave-to {
    opacity: 0;
    transform: translate(0%, 0);
}

.smooth-list-enter-to,
.smooth-list-leave-from {
    opacity: 1;
    transform: translate(0, 0);
}

.smooth-list-enter-active,
.smooth-list-leave-active {
    transition: all .4s var(--ease-out-cubic);
}

.smooth-list-leave-active {
    position: absolute !important;
}

.smooth-list-move {
    transition: all .3s var(--ease-out-cubic);
}
</style>
